import { useTheme } from '@mui/material';
import FormControl, { FormControlProps } from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import { InputBaseProps } from '@mui/material/InputBase';
import Radio from '@mui/material/Radio';
import RadioGroup, { RadioGroupProps } from '@mui/material/RadioGroup';
import { capitalize, get } from 'lodash-es';
import { InputHTMLAttributes, ReactNode } from 'react';
import {
  Controller,
  FieldErrors,
  FieldValues,
  UseControllerProps,
} from 'react-hook-form';

interface FormFieldProps<T extends FieldValues> extends UseControllerProps<T> {
  errors: FieldErrors<T>;
  inputProps?: InputBaseProps['inputProps'];
  label?: string;
  list: (string | { label: ReactNode; value: string })[];
  radioGroupSx?: RadioGroupProps['sx'];
  sx?: FormControlProps['sx'];
}

const RadioGroupFormField = <T extends FieldValues>({
  control,
  disabled,
  errors,
  inputProps,
  label,
  list,
  name,
  radioGroupSx,
  sx,
}: FormFieldProps<T>) => {
  const theme = useTheme();

  return (
    <>
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          const hasError = Boolean(get(errors, name)?.message);

          return (
            <FormControl error={Boolean(hasError)} sx={{ ...sx }}>
              {label && <FormLabel>{label}</FormLabel>}

              <RadioGroup
                row
                {...field}
                onChange={(event) => {
                  field.onChange(event.target.value);
                }}
                sx={radioGroupSx}
              >
                {list.map((unit, idx) => (
                  <FormControlLabel
                    key={typeof unit === 'string' ? unit : unit.value}
                    value={typeof unit === 'string' ? unit : unit.value}
                    control={
                      <Radio
                        disabled={disabled}
                        slotProps={{
                          input: {
                            ...inputProps,
                            'data-test-id': `${get(
                              inputProps,
                              'data-test-id',
                              '',
                            )}__${idx}`,
                          } as unknown as InputHTMLAttributes<HTMLInputElement>,
                        }}
                        sx={
                          hasError
                            ? { '&.MuiRadio-root': { color: theme.palette.error.main } }
                            : undefined
                        }
                      />
                    }
                    label={typeof unit === 'string' ? capitalize(unit) : unit.label}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          );
        }}
      />
    </>
  );
};

export { RadioGroupFormField };
